import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { ZoomIn, ZoomOut, RotateCw, Check, X, Crop as CropIcon, Moon, Sun, Sliders } from 'lucide-react';

/**
 * Helper function to create HTML Image element from URL
 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

/**
 * Helper function to get cropped image Blob / File from Canvas with darkness/opacity applied
 */
export async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  darkness = 0,
  fileName = 'cropped-photo.jpg'
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  const rotRad = (rotation * Math.PI) / 180;

  const bBoxWidth = Math.abs(Math.cos(rotRad) * image.width) + Math.abs(Math.sin(rotRad) * image.height);
  const bBoxHeight = Math.abs(Math.sin(rotRad) * image.width) + Math.abs(Math.cos(rotRad) * image.height);

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);

  ctx.drawImage(image, 0, 0);

  const cropCanvas = document.createElement('canvas');
  const cropCtx = cropCanvas.getContext('2d');

  if (!cropCtx) {
    throw new Error('Crop Canvas context not available');
  }

  cropCanvas.width = pixelCrop.width;
  cropCanvas.height = pixelCrop.height;

  cropCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  if (darkness > 0) {
    cropCtx.fillStyle = `rgba(0, 0, 0, ${darkness / 100})`;
    cropCtx.fillRect(0, 0, pixelCrop.width, pixelCrop.height);
  }

  return new Promise((resolve) => {
    cropCanvas.toBlob((blob) => {
      if (!blob) {
        resolve(null);
        return;
      }
      const croppedFile = new File([blob], fileName, { type: 'image/jpeg' });
      const previewUrl = URL.createObjectURL(blob);
      resolve({ file: croppedFile, blob, url: previewUrl });
    }, 'image/jpeg', 0.92);
  });
}

/**
 * Premium Modern Image Cropper & Resizer Modal
 */
export default function ImageCropperModal({
  imageSrc,
  onClose,
  onCropComplete,
  title = 'Crop & Edit Image',
  initialAspect = 1,
  cropShape = 'rect',
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [darkness, setDarkness] = useState(0);
  const [aspect, setAspect] = useState(initialAspect);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCropChange = (newCrop) => setCrop(newCrop);
  const handleZoomChange = (newZoom) => setZoom(newZoom);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixelsParam) => {
    setCroppedAreaPixels(croppedAreaPixelsParam);
  }, []);

  const handleApplyCrop = async () => {
    if (!croppedAreaPixels || !imageSrc) return;
    try {
      setIsProcessing(true);
      const croppedData = await getCroppedImg(imageSrc, croppedAreaPixels, rotation, darkness, 'cropped-photo.jpg');
      if (croppedData && onCropComplete) {
        onCropComplete(croppedData);
      }
    } catch (err) {
      console.error('Failed to crop image:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        background: 'rgba(7, 10, 19, 0.88)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      {/* Scope Range Input & Custom Cropper Styles */}
      <style>{`
        .custom-cropper-slider {
          -webkit-appearance: none;
          width: 100%;
          height: 6px;
          border-radius: 4px;
          background: #334155;
          outline: none;
          transition: background 0.15s ease;
        }
        .custom-cropper-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #3b82f6;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.5);
          cursor: pointer;
          transition: transform 0.1s ease;
        }
        .custom-cropper-slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
          background: #60a5fa;
        }
        .crop-aspect-btn {
          background: transparent;
          color: #94a3b8;
          border: none;
          padding: 6px 14px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .crop-aspect-btn:hover {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.08);
        }
        .crop-aspect-btn.active {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: #ffffff;
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
        }
      `}</style>

      <div
        style={{
          background: '#0f172a',
          width: '100%',
          maxWidth: 620,
          borderRadius: 24,
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.12)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header - High Contrast & Sleek */}
        <div
          style={{
            padding: '18px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 16px rgba(37, 99, 235, 0.4)',
              }}
            >
              <CropIcon size={20} color="#ffffff" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.01em' }}>
                {title}
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#cbd5e1', fontWeight: 500 }}>
                Adjust crop region, zoom & darkness opacity
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              color: '#cbd5e1',
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
              e.currentTarget.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              e.currentTarget.style.color = '#cbd5e1';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Studio Cropper Viewport */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 360,
            background: '#020617',
            boxShadow: 'inset 0 0 30px rgba(0,0,0,0.8)',
          }}
        >
          {imageSrc && (
            <>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect}
                cropShape={cropShape}
                showGrid={true}
                onCropChange={handleCropChange}
                onZoomChange={handleZoomChange}
                onCropComplete={onCropCompleteCallback}
              />
              {/* Live Darkness Overlay */}
              {darkness > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    backgroundColor: `rgba(0, 0, 0, ${darkness / 100})`,
                    zIndex: 2,
                    transition: 'background-color 0.1s ease',
                  }}
                />
              )}
            </>
          )}
        </div>

        {/* Sleek Controls Panel */}
        <div
          style={{
            padding: '20px 24px',
            background: '#0f172a',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          {/* Zoom Slider Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#e2e8f0',
                width: 75,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Sliders size={14} color="#3b82f6" /> Zoom
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <ZoomOut size={16} color="#94a3b8" />
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="custom-cropper-slider"
              />
              <ZoomIn size={16} color="#94a3b8" />
            </div>

            <button
              onClick={() => setRotation((r) => (r + 90) % 360)}
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                color: '#f8fafc',
                padding: '7px 14px',
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)')}
            >
              <RotateCw size={14} color="#60a5fa" /> Rotate
            </button>
          </div>

          {/* Darkness / Opacity Slider Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#e2e8f0',
                width: 75,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Moon size={14} color="#f59e0b" /> Darken
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <Sun size={16} color="#f59e0b" />
              <input
                type="range"
                min={0}
                max={85}
                step={1}
                value={darkness}
                onChange={(e) => setDarkness(Number(e.target.value))}
                className="custom-cropper-slider"
              />
              <Moon size={16} color="#60a5fa" />
            </div>

            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: darkness > 0 ? '#60a5fa' : '#64748b',
                background: darkness > 0 ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                padding: '4px 10px',
                borderRadius: 8,
                border: darkness > 0 ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)',
                minWidth: 70,
                textAlign: 'center',
              }}
            >
              {darkness}% Opacity
            </span>
          </div>

          {/* Aspect Ratio Segmented Control */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 2 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', width: 75 }}>Preset:</span>

            <div
              style={{
                display: 'flex',
                background: 'rgba(2, 6, 23, 0.6)',
                padding: 4,
                borderRadius: 12,
                border: '1px solid rgba(255, 255, 255, 0.08)',
                flex: 1,
                justifyContent: 'space-between',
              }}
            >
              {[
                { label: '1:1 Square (DP)', value: 1 },
                { label: '4:3 Product', value: 4 / 3 },
                { label: '16:9 Banner', value: 16 / 9 },
                { label: 'Free Aspect', value: undefined },
              ].map((ratio) => {
                const isActive = aspect === ratio.value;
                return (
                  <button
                    key={ratio.label}
                    onClick={() => setAspect(ratio.value)}
                    className={`crop-aspect-btn ${isActive ? 'active' : ''}`}
                    style={{ flex: 1, textAlign: 'center' }}
                  >
                    {ratio.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer Action Bar */}
        <div
          style={{
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12,
            background: '#0f172a',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <button
            onClick={onClose}
            disabled={isProcessing}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.16)',
              color: '#cbd5e1',
              padding: '10px 20px',
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
              e.currentTarget.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#cbd5e1';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleApplyCrop}
            disabled={isProcessing}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              border: 'none',
              color: '#ffffff',
              padding: '10px 24px',
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              boxShadow: '0 4px 18px rgba(37, 99, 235, 0.45)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            {isProcessing ? (
              'Processing...'
            ) : (
              <>
                <Check size={18} strokeWidth={2.5} /> Crop & Save Image
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
